/**
 * MediaField — iter-64.
 *
 * Two-in-one form field for image/video/pdf URLs:
 *   • the operator can either paste an external URL, OR
 *   • upload a file directly from their computer.
 *
 * Uploads go through the existing `/api/staff/media-library` endpoint, which
 * stores the asset in the shared S3-compatible object store and returns a
 * relative URL such as `/api/media-library/<uuid>`. The widget then serialises
 * that URL to the parent editor via `onChange`.
 *
 * Ready-made previews are rendered for images and videos so operators can
 * confirm the asset visually before publishing the section.
 */
import { useRef, useState } from "react";
import { Upload, Loader2, Image as ImageIcon, Video as VideoIcon, File as FileIcon, X } from "lucide-react";
import { toast } from "sonner";
import { API, getStaffToken } from "../../lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

export default function MediaField({
  label,
  value,
  onChange,
  accept = "image/*",       // "image/*" | "video/*" | "image/*,video/*" | "application/pdf"
  kind = "image",           // "image" | "video" | "pdf" | "any"
  testid = "media-field",
  placeholder = "https://…",
  hint,                     // small helper text under the input
  universe = "",
  offer = "",
}) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  async function handleFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (universe) fd.append("universe", universe);
      if (offer) fd.append("offer", offer);
      const token = getStaffToken();
      const r = await fetch(`${API}/staff/media-library`, {
        method: "POST",
        body: fd,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!r.ok) {
        const data = await r.json().catch(() => ({}));
        throw new Error(data.detail || `Upload échoué (${r.status})`);
      }
      const doc = await r.json();
      // Full absolute URL so it works on both the vitrine and staff sides.
      const abs = doc.url.startsWith("http") ? doc.url : `${BACKEND_URL}${doc.url}`;
      onChange(abs);
      toast.success("Fichier téléchargé");
    } catch (err) {
      toast.error(err.message || "Upload impossible");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  }

  const resolvedUrl = value ? (value.startsWith("http") ? value : `${BACKEND_URL}${value}`) : "";
  const isImage = kind === "image" || (kind === "any" && /\.(png|jpe?g|gif|webp|avif)$/i.test(value || ""));
  const isVideo = kind === "video" || (kind === "any" && /\.(mp4|webm|mov|m4v|ogg)$/i.test(value || ""));

  return (
    <label className="block" data-testid={testid}>
      <span className="block text-[0.55rem] tracking-[0.3em] uppercase text-[#0A0A0A]/55 mb-1.5">
        {label}
      </span>

      <div className="border border-[#0A0A0A]/15 bg-white overflow-hidden">
        {/* Preview area */}
        {value ? (
          <div className="relative bg-[#0A0A0A]/5" data-testid={`${testid}-preview`}>
            {isImage ? (
              <img src={resolvedUrl} alt="" className="w-full h-40 object-cover" />
            ) : isVideo ? (
              <video src={resolvedUrl} className="w-full h-40 object-cover" muted playsInline preload="metadata" />
            ) : (
              <div className="w-full h-24 flex items-center justify-center text-[#0A0A0A]/45 text-sm">
                <FileIcon size={16} className="mr-2" /> Fichier téléchargé
              </div>
            )}
            <button
              type="button"
              onClick={() => onChange("")}
              className="absolute top-2 right-2 bg-white/95 hover:bg-white p-1.5 shadow"
              aria-label="Retirer le média"
              data-testid={`${testid}-clear`}
            >
              <X size={13} strokeWidth={1.5} />
            </button>
          </div>
        ) : (
          <div className="w-full h-24 flex items-center justify-center gap-2 text-[#0A0A0A]/35 text-xs bg-[#FAF7F2]">
            {kind === "video" ? <VideoIcon size={16} /> : kind === "pdf" ? <FileIcon size={16} /> : <ImageIcon size={16} />}
            Aucun fichier
          </div>
        )}

        {/* Upload button */}
        <div className="flex items-stretch border-t border-[#0A0A0A]/8">
          <input
            ref={inputRef}
            type="file"
            accept={accept}
            onChange={handleFile}
            className="hidden"
            data-testid={`${testid}-file`}
          />
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            className="flex-shrink-0 inline-flex items-center gap-2 px-3 py-2 text-[0.6rem] tracking-[0.3em] uppercase text-[#0A0A0A] hover:text-[#B8922A] border-r border-[#0A0A0A]/8 disabled:opacity-50"
            data-testid={`${testid}-upload`}
          >
            {uploading ? (
              <><Loader2 size={12} className="animate-spin" /> Envoi…</>
            ) : (
              <><Upload size={12} /> Choisir un fichier</>
            )}
          </button>
          <input
            type="url"
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            className="flex-1 px-3 py-2 text-sm focus:outline-none focus:bg-[#FAF7F2]/50"
            data-testid={`${testid}-url`}
          />
        </div>
      </div>

      {hint && (
        <span className="block text-[10px] text-[#0A0A0A]/45 mt-1">{hint}</span>
      )}
    </label>
  );
}
